/**
 * Runtime Service
 * Connects to the SimCheck Runtime for flow execution
 */

import { auth } from '@/config/firebase';
import type { ForcedRat } from '@simcheck/shared-types';
import { RUNTIME_API_URL } from '@/config/env';

// Runtime API configuration — RUNTIME_API_URL is the executeFlow function (config/env).

/**
 * Get authentication headers with Firebase ID token
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const idToken = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
  };
}

export interface ExecuteFlowRequest {
  flowId: string;
  flowDefinition?: any; // For primitives, send the flow definition
  inputs?: Record<string, any>;
  userId: string;
  organizationId: string;
  locationId?: string; // Optional location/edge ID for execution
  executionContext?: { // Device and modem for execution (all optional - runtime allocates if needed)
    deviceId?: string;
    modemImei?: string;
    eSimProfileId?: string;
    /** Group test: send the eSIM GROUP id and let the backend resolve a member
     *  per allocated modem (reuse one already installed there, else install an
     *  available one). Mutually exclusive with eSimProfileId in practice. */
    eSimGroupId?: string;
    /** Force a Radio Access Technology pin before the flow runs.
     *  Backend filters modem allocation by capability, then dispatches a
     *  rat-lock RPC to the Pi. Hard-fails the flow on any lock error. */
    forcedRat?: ForcedRat;
    /** Restrict modem allocation to a specific edge device. Used by admin
     *  features that need the read to come from a known Pi (e.g. "Discover
     *  Location") rather than any free modem at the location. */
    forcedDeviceId?: string;
  };
  useEmulator?: boolean; // Optional flag to use emulator/mock modem mode
  testRunId?: string; // Link to test_run document for status sync
  source?: string; // Origin of execution (e.g., 'editor', 'quick-test', 'schedule')
}

export interface ExecutionResponse {
  executionId: string;
  status: string;
  message?: string;
}

export interface ExecutionStatus {
  executionId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt?: string;
  completedAt?: string;
  result?: any;
  logs?: Array<{
    level: 'info' | 'error' | 'warning' | 'debug';
    message: string;
    timestamp: any;
  }>;
  duration?: number;
}

/**
 * Execute a flow on the runtime
 * Sends the complete flow definition to the runtime (no shared database)
 */
export async function executeFlow(
  flowDefinition: any,
  inputs: Record<string, any> = {},
  userId: string,
  organizationId: string,
  executionContext?: {
    deviceId?: string;
    modemImei?: string;
    eSimProfileId?: string;
    /** Group test: send the eSIM GROUP id and let the backend resolve a member
     *  per allocated modem (reuse one already installed there, else install an
     *  available one). Mutually exclusive with eSimProfileId in practice. */
    eSimGroupId?: string;
    forcedRat?: ForcedRat;
    /**
     * Force the modem to register on this PLMN ("MCCMNC", e.g. "42501")
     * BEFORE the flow's body runs. The backend dispatches a network-lock
     * RPC after allocation; on failure the run is rolled back the same way
     * a TLAM session is. Was previously TLAM-only.
     */
    forcedNetworkMccMnc?: string;
    forcedDeviceId?: string;
    /** Approve moving a limited-download eSIM to another modem (#375). */
    allowEsimRelocation?: boolean;
  },
  locationId?: string,
  useEmulator?: boolean,
  testRunId?: string,
  source?: string
): Promise<ExecutionResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(RUNTIME_API_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      flowDefinition,
      flowId: flowDefinition.id || 'adhoc-flow',
      inputs,
      userId,
      organizationId,
      executionContext,
      locationId,
      useEmulator,
      testRunId,
      source,
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    // Keep the structured fields (reason, approvalRequired, …) so callers can
    // act on them — e.g. ask to approve an eSIM move and resubmit.
    throw Object.assign(new Error(error.message || 'Failed to execute flow'), {
      status: response.status,
      code: error.error,
      reason: error.reason,
      approvalRequired: error.approvalRequired === true,
    });
  }

  return response.json();
}

/**
 * Execute a primitive (test mode for primitive editor)
 */
export async function executePrimitive(
  primitiveId: string,
  inputs: Record<string, any> = {},
  userId?: string,
  useEmulator: boolean = true
): Promise<ExecutionResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${RUNTIME_API_URL}/executePrimitive`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      primitiveId,
      inputs,
      userId,
      useEmulator
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to execute primitive');
  }

  return response.json();
}

/**
 * Get primitive execution status
 */
export async function getPrimitiveExecutionStatus(executionId: string): Promise<ExecutionStatus> {
  // Use the primitive_executions collection
  // Note: This shares the same getExecutionStatus endpoint but queries different collection
  const headers = await getAuthHeaders();

  const response = await fetch(`${RUNTIME_API_URL}/getPrimitiveExecutionStatus?executionId=${executionId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    // Fall back to regular execution status if endpoint doesn't exist yet
    return getExecutionStatus(executionId);
  }

  return response.json();
}

/**
 * Get execution status
 */
export async function getExecutionStatus(executionId: string): Promise<ExecutionStatus> {
  // RUNTIME_API_URL points at the executeFlow function. The status function
  // shares the same project — derive its URL by handling both URL forms:
  //   v1-style: https://us-central1-<proj>.cloudfunctions.net/executeFlow
  //   v2-style: https://executeflow-<hash>-uc.a.run.app
  // We try a path-segment swap first (works for v1) then a hostname swap
  // (works for v2's lowercase Cloud Run URL).
  const runtimeUrl = RUNTIME_API_URL;
  let statusUrl = runtimeUrl.replace('/executeFlow', '/getExecutionStatus');
  if (statusUrl === runtimeUrl) {
    statusUrl = runtimeUrl.replace(/\/\/executeflow-/, '//getexecutionstatus-');
  }
  const headers = await getAuthHeaders();

  const response = await fetch(`${statusUrl}?executionId=${executionId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get execution status');
  }

  return response.json();
}

/**
 * Cancel a running execution
 */
export async function cancelExecution(executionId: string): Promise<void> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${RUNTIME_API_URL}/cancelExecution`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ executionId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to cancel execution');
  }
}

/**
 * Get the latest editor execution for a specific flow and user.
 * Used to restore execution state when returning to the flow editor.
 */
export async function getLatestEditorExecution(
  flowId: string,
  userId: string,
  organizationId: string
): Promise<ExecutionStatus | null> {
  const { getFirestore, collection, query, where, orderBy, limit, getDocs } = await import('firebase/firestore');
  const { db } = await import('@/config/firebase');

  // The organizationId filter is what lets the executions read rule admit
  // this query (rules are not filters; see canReadOrgRecord).
  const q = query(
    collection(db, 'executions'),
    where('organizationId', '==', organizationId),
    where('flowId', '==', flowId),
    where('userId', '==', userId),
    where('source', '==', 'editor'),
    orderBy('startedAt', 'desc'),
    limit(1)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const doc = snapshot.docs[0];
  const data = doc.data();
  return {
    executionId: data.id,
    status: data.status,
    startedAt: data.startedAt?.toDate?.()?.toISOString() || data.startedAt,
    completedAt: data.completedAt?.toDate?.()?.toISOString() || data.completedAt,
    result: data.result,
    logs: data.logs,
    duration: data.duration,
  };
}

/**
 * Poll execution status until completion
 */
export async function pollExecutionStatus(
  executionId: string,
  onUpdate?: (status: ExecutionStatus) => void,
  intervalMs: number = 1000
): Promise<ExecutionStatus> {
  return new Promise((resolve, reject) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await getExecutionStatus(executionId);
        
        if (onUpdate) {
          onUpdate(status);
        }

        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          clearInterval(pollInterval);
          resolve(status);
        }
      } catch (error) {
        clearInterval(pollInterval);
        reject(error);
      }
    }, intervalMs);

    // Timeout after 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      reject(new Error('Execution polling timeout'));
    }, 600000);
  });
}

